<p-dialog
  [(visible)]="visible"
  [modal]="true"
  [style]="{ width: '50vw' }"
  [breakpoints]="{ '960px': '75vw', '640px': '90vw' }"
  (onHide)="closeDialog()"
  [draggable]="false"
  [resizable]="false">
  <ng-template pTemplate="header">
    <div class="d-flex align-items-center gap-2">
      <p-avatar
        [label]="getInitials(partner()?.user?.name)"
        styleClass="mr-2"
        size="large"
        [style]="{ 'background-color': '#857031', color: '#ffffff' }">
      </p-avatar>
      <div>
        <h3 class="m-0 text-black">{{ partner()?.user?.name | titlecase}}</h3>
        <small class="text-500 text-black">{{ partner()?.user?.email }}</small>
      </div>
    </div>
  </ng-template>

  @if (partner(); as partnerData) {
    <div class="partner-details">
      <!-- Status Section -->
      <div class="section">
        <h4 class="section-title">Status Overview</h4>
        <div class="grid overview-section">
          <div class="col-12 md:col-6 item-row">
            <label class="detail-label">Application Status</label>
            <p-tag
              [value]="partnerData.status"
              [styleClass]="getStatusSeverity(partnerData.status)"
              styleClass="text-sm"/>
          </div>
          <div class="col-12 md:col-6 item-row">
            <label class="detail-label">KYC Status</label>
            <p-tag
              [value]="partnerData.kycStatus"
              [styleClass]="getKycSeverity(partnerData.kycStatus)"
              styleClass="text-sm"/>
          </div>
          <div class="col-12 md:col-6 item-row">
            <label class="detail-label">KYC Verified</label>
            <div class="flex align-items-center gap-2">
              <i
                [class]="
                  partnerData.kycVerificationResult.verified
                    ? 'pi pi-check-circle text-green-500'
                    : 'pi pi-times-circle text-red-500'
                "></i>
              <span>{{
                partnerData.kycVerificationResult.verified ? "Yes" : "No"
              }}</span>
            </div>
          </div>
          <div class="col-12 md:col-6 item-row">
            <label class="detail-label">Can Approve</label>
            <div class="flex align-items-center gap-2">
              <i
                [class]="
                  partnerData.canApprove
                    ? 'pi pi-check-circle text-green-500'
                    : 'pi pi-times-circle text-red-500'
                "></i>
              <span>{{ partnerData.canApprove ? "Yes" : "No" }}</span>
            </div>
          </div>
        </div>
      </div>

      <p-divider></p-divider>

      <!-- Business Information -->
      <div class="section">
        <h4 class="section-title">Business Information</h4>
        <div class="overview-section">
          <div class="detail-group item-row">
            <label class="detail-label">Partnership Goals</label>
            <p class="detail-value">
              {{ partnerData.partnershipGoals || "Not specified" }}
            </p>
          </div>
          <div class="detail-group item-row">
            <label class="detail-label">Target Audience</label>
            <p class="detail-value">
              {{ partnerData.targetAudience || "Not specified" }}
            </p>
          </div>
          <div class="detail-group item-row">
            <label class="detail-label">Marketing Plan</label>
            <p class="detail-value">
              {{ partnerData.marketingPlan || "Not specified" }}
            </p>
          </div>
        </div>
      </div>

      <p-divider></p-divider>

      <!-- Social Media -->
      <div class="section">
        <h4 class="section-title">Social Media</h4>
        @if (partnerData.socialMedia && partnerData.socialMedia.length > 0) {
          <div class="grid">
            @for (social of partnerData.socialMedia; track $index) {
              <div class="col-12">
                <div class="social-links">
                  @if (social.instagram) {
                    <div class="social-item">
                      <i class="pi pi-instagram text-pink-500"></i>
                      <a
                        [href]="'https://instagram.com/' + social.instagram"
                        target="_blank"
                        class="social-link">
                        {{ social.instagram }}
                      </a>
                    </div>
                  }
                  @if (social.facebook) {
                    <div class="social-item">
                      <i class="pi pi-facebook text-blue-600"></i>
                      <a
                        [href]="'https://facebook.com/' + social.facebook"
                        target="_blank"
                        class="social-link">
                        {{ social.facebook }}
                      </a>
                    </div>
                  }
                  @if (social.twitter) {
                    <div class="social-item">
                      <i class="pi pi-twitter text-blue-400"></i>
                      <a
                        [href]="'https://twitter.com/' + social.twitter"
                        target="_blank"
                        class="social-link">
                        {{ social.twitter }}
                      </a>
                    </div>
                  }
                </div>
              </div>
            }
          </div>
        } @else {
          <p class="text-500">No social media links provided</p>
        }
      </div>

      <p-divider></p-divider>

      <!-- Stripe Information -->
      <div class="section">
        <h4 class="section-title">Payment Information</h4>
        <div class="grid">
          <div class="col-12 md:col-6 item-row">
            <label class="detail-label">Stripe Account ID</label>
            <p class="detail-value">
              {{ partnerData.stripeAccountId || "Not set up" }}
            </p>
          </div>
          <div class="col-12 md:col-6 item-row">
            <label class="detail-label">Stripe Setup Completed</label>
            <div class="flex align-items-center gap-2">
              <i
                [class]="
                  partnerData.stripeAccountSetupCompleted
                    ? 'pi pi-check-circle text-green-500'
                    : 'pi pi-times-circle text-red-500'
                "></i>
              <span>{{
                partnerData.stripeAccountSetupCompleted ? "Yes" : "No"
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <p-divider></p-divider>

      <!-- Review Information -->
      <div class="section">
        <h4 class="section-title">Review Information</h4>
        <div class="grid">
          <div class="col-12 md:col-6 item-row">
            <label class="detail-label">Submitted At</label>
            <p class="detail-value">
              {{ partnerData.submittedAt | date: "medium" }}
            </p>
          </div>
          <div class="col-12 md:col-6 item-row">
            <label class="detail-label">Reviewed At</label>
            <p class="detail-value">
              {{
                partnerData.reviewedAt
                  ? (partnerData.reviewedAt | date: "medium")
                  : "Not reviewed yet"
              }}
            </p>
          </div>
          @if (partnerData.reviewedBy) {
            <div class="col-12 item-row">
              <label class="detail-label">Reviewed By</label>
              <p class="detail-value">{{ partnerData.reviewedBy }}</p>
            </div>
          }
          @if (partnerData.adminNotes) {
            <div class="col-12">
              <label class="detail-label">Admin Notes</label>
              <p class="detail-value admin-notes">
                {{ partnerData.adminNotes }}
              </p>
            </div>
          }
        </div>
      </div>
    </div>
  }
</p-dialog>
